import { EMPTY_TITLE_CONTEXT, WIDGET_PRESET_KEY, type WidgetTitleContext } from "@salestrak/ui";
import { isMeasureFn } from "@salestrak/widgets";
import {
  WIDGET_DIMENSION_OPTIONS,
  isFieldDimension,
  type WidgetDimension,
} from "#app/widgets/dashboards/use-dimension-counts";
import {
  DIMENSION_FIELDS_SOURCE,
  MEASURE_FIELDS_SOURCE,
  MEASURE_FN_LABEL,
  measureFieldId,
} from "#app/widgets/dashboards/widget-measure";

/** What a title derives from: the stored Group by choice, which is a dimension. */
type TitledDimension = WidgetDimension;

/** The resolved config a `dynamicTitle` receives: the module's own keys plus
 *  the passthrough ones the chrome writes (`title`, the preset stamp, a seed's
 *  `widgetKey`). */
export type TitledConfig = Record<string, unknown>;

/**
 * A row placed from a Custom-section card ("Bar chart", "Line chart"): the
 * gallery stamps `preset: null` on a free-form entry and the entry's key on a
 * locking one, so `null` is the mark of a blank card. A seeded row (a
 * `widgetKey`) is a template's, never a blank card, whatever its stamp. A row
 * with NO stamp predates stamping and cannot be told apart from a curated
 * placement, so it is not blank here and keeps deriving its title.
 */
export function isBlankPlacement(config: TitledConfig): boolean {
  return config[WIDGET_PRESET_KEY] === null && typeof config.widgetKey !== "string";
}

/**
 * Title mechanics for the dimension-grouped widgets (`dynamicTitle` on each
 * module — the widget owns title resolution entirely, the chrome just displays
 * what it returns). The rule each resolver implements: a stored `config.title`
 * is honored ONLY when it reads as user-authored — anything the widget family
 * itself has ever generated (any grouping's derived form, past or present
 * spellings, old seeds included) is ignored and re-derived from the live
 * `groupBy`. So a title tracks regrouping until a user renames the widget,
 * and a rename is never overwritten.
 */
const DIMENSION_NOUN: Record<string, string> = Object.fromEntries(
  WIDGET_DIMENSION_OPTIONS.map((o) => [o.value, o.label.toLowerCase()]),
);

/** A field dimension's noun when the title context cannot name the field (no
 *  context, or a field the workspace no longer has). With a context the noun
 *  is the field's own name, as the user spelled it ("Bid Type"). */
const FIELD_NOUN = "field";

/** Nouns a dimension WAS called. A title generated under one still belongs to
 *  the family, so it re-derives to the current spelling instead of sticking. */
const RETIRED_DIMENSION_NOUNS = ["age health"];

/** Every noun a dimension title may carry, current and retired. */
const FAMILY_NOUNS = [...Object.values(DIMENSION_NOUN), FIELD_NOUN, ...RETIRED_DIMENSION_NOUNS];

function dimensionNoun(dimension: TitledDimension, context: WidgetTitleContext): string {
  if (isFieldDimension(dimension)) {
    return context.optionLabel(DIMENSION_FIELDS_SOURCE, dimension) ?? FIELD_NOUN;
  }
  return DIMENSION_NOUN[dimension] ?? dimension;
}

/**
 * What a field Measure is called in a title: the field's name for a sum
 * ("Deductible"), the aggregate word before it otherwise ("Average
 * Deductible"). Null for a count, a config without the measure keys, or a
 * field the context cannot name — the title then falls back to the family's
 * count noun ("Workload", "Projects"). `key` is the config key the module
 * stores its Measure under (`measure`, or the line's `figure`).
 */
function measureSubject(
  config: TitledConfig,
  context: WidgetTitleContext,
  key = "measure",
): string | null {
  const measure = config[key];
  const fieldId = measureFieldId(typeof measure === "string" ? measure : null);
  if (fieldId === null) return null;
  const name = context.optionLabel(MEASURE_FIELDS_SOURCE, fieldId);
  if (name === undefined) return null;
  const fn = isMeasureFn(config.calculate) ? config.calculate : "sum";
  return fn === "sum" ? name : `${MEASURE_FN_LABEL[fn]} ${name}`;
}

/** The two halves of a "<subject> by <noun>" title, or null for any other shape. */
function splitBy(title: string): { subject: string; noun: string } | null {
  const at = title.lastIndexOf(" by ");
  if (at <= 0) return null;
  return { subject: title.slice(0, at), noun: title.slice(at + 4) };
}

/**
 * Whether a "<subject> by <noun>" title is one this family could have
 * generated for THIS card: the count noun (`countNoun`, "Workload") or the
 * current measure subject on the left, a dimension noun or the current field
 * name on the right. Either half alone claims it — a stored "Deductible by
 * Bid Type" has to keep re-deriving after the card regroups to stage AND
 * after its Measure goes back to the count, and each of those changes
 * invalidates one half — so a user title that happens to read "Workload by
 * crew" re-derives too. Field names outside the current config cannot be
 * recognized: a title committed under a field the card since dropped sticks
 * like a rename.
 */
function ownsByTitle(
  title: string,
  countNoun: string,
  subject: string | null,
  noun: string,
): boolean {
  const halves = splitBy(title);
  if (!halves) return false;
  const leftOwned = halves.subject === countNoun || halves.subject === subject;
  const rightOwned = FAMILY_NOUNS.includes(halves.noun) || halves.noun === noun;
  return leftOwned || rightOwned;
}

/** A blank card derives its title once a custom field is involved — the
 *  gallery name says nothing about a "Deductible by Bid Type" — and keeps the
 *  gallery name while every choice is built in. */
function involvesField(groupBy: TitledDimension, subject: string | null): boolean {
  return isFieldDimension(groupBy) || subject !== null;
}

/**
 * A Custom card lands with its gallery label STORED as the title
 * (`presetInitialConfig`), so the label has to count as the family's own
 * once a field is involved, or a placed "Bar chart" would never become
 * "Deductible by Bid Type". While no field is involved the label is not
 * owned: the card keeps the name the user just picked, and a rename to that
 * very label stands.
 */
function ownsBlankName(title: string, blank: string, fieldInvolved: boolean): boolean {
  return fieldInvolved && title === blank;
}

/**
 * The one title rule every resolver applies: a stored title is honored only
 * when it reads as user-authored — i.e. the widget's own family (`ownsStored`)
 * doesn't claim it. Anything the widget generated re-derives from the live
 * config, so titles track setting changes until a real rename.
 *
 * A blank-card placement (`isBlankPlacement`) is the exception on both counts.
 * The family never generates a title for one, so ANY stored title on it is
 * the user's and stands, even one spelled like a derived form; and an
 * untitled one shows `blank` — the gallery name of the card it came from —
 * not a config-derived phrase. Rows placed before Custom cards stored their
 * label read the same as rows placed after. Families with no blank card omit
 * `blank`.
 */
export function resolveStoredTitle(
  config: TitledConfig,
  ownsStored: (title: string) => boolean,
  derived: string,
  blank?: string,
): string {
  const stored = config.title;
  const isBlank = blank !== undefined && isBlankPlacement(config);
  if (typeof stored === "string" && stored.length > 0 && (isBlank || !ownsStored(stored))) {
    return stored;
  }
  return isBlank ? blank : derived;
}

/** "Workload by stage", or under a field Measure the measure's own subject —
 *  "Deductible by Bid Type", "Average Deductible by stage". */
export function workloadByDimensionTitle(
  groupBy: TitledDimension,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
  subject: string | null = null,
): string {
  return `${subject ?? "Workload"} by ${dimensionNoun(groupBy, context)}`;
}

/** Every title the workload family has ever generated for any grouping — the
 *  "<subject> by <noun>" forms plus the pre-release seed spellings
 *  ("Workload", "Work by <x>"). */
function isGeneratedWorkloadTitle(
  title: string,
  subject: string | null,
  noun: string,
): boolean {
  if (title === "Workload") return true;
  if (FAMILY_NOUNS.some((family) => title === `Work by ${family}`)) return true;
  return ownsByTitle(title, "Workload", subject, noun);
}

/** `dynamicTitle` for dimension-bar / type-mix / dimension-battery. `blank` is
 *  the module's Custom-card name ("Bar chart"); `context` names the fields
 *  the config stores by id. */
export function workloadTitle(
  config: TitledConfig,
  groupBy: TitledDimension,
  blank: string,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
): string {
  const subject = measureSubject(config, context);
  const noun = dimensionNoun(groupBy, context);
  const fieldInvolved = involvesField(groupBy, subject);
  return resolveStoredTitle(
    config,
    (title) =>
      ownsBlankName(title, blank, fieldInvolved) || isGeneratedWorkloadTitle(title, subject, noun),
    workloadByDimensionTitle(groupBy, context, subject),
    fieldInvolved ? undefined : blank,
  );
}

/** The Custom cards' gallery names, which `presetInitialConfig` stores as the
 *  placed row's title. */
const LINE_BLANK_NAME = "Line chart";
const PORTFOLIO_BLANK_NAME = "Treemap";

const OVER_TIME_GROUPINGS = [
  "none",
  "stage",
  "type",
  "workflow",
  "assignee",
  "team",
  "source",
  "outcome",
] as const;
type OverTimeGrouping = (typeof OVER_TIME_GROUPINGS)[number];

/** series-line's split vocabulary is its own (+ "none" = one total line). An
 *  outcome split counts Updates, so its title says so and ignores the
 *  measure; under a field Measure the count noun gives way to the subject —
 *  "Deductible over time", "Average Deductible by stage over time". */
export function overTimeTitle(groupBy: OverTimeGrouping, subject: string | null = null): string {
  if (groupBy === "outcome") return "Outcomes over time";
  if (groupBy === "none") return `${subject ?? "Projects"} over time`;
  if (subject !== null) return `${subject} by ${groupBy} over time`;
  return `${groupBy.charAt(0).toUpperCase()}${groupBy.slice(1)} over time`;
}

/** The line's Measure lives under `figure`; `measure` is its date column. */
const LINE_MEASURE_KEY = "figure";

/** `dynamicTitle` for series-line — same rule over its own title family. A
 *  "<subject> … over time" title is the family's when the subject is the
 *  current one or the split is a known grouping, so the title follows both a
 *  regroup and a Measure change. */
export function seriesLineTitle(
  config: TitledConfig,
  groupBy: OverTimeGrouping,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
): string {
  const subject = measureSubject(config, context, LINE_MEASURE_KEY);
  const owns = (title: string): boolean => {
    if (ownsBlankName(title, LINE_BLANK_NAME, subject !== null)) return true;
    if (OVER_TIME_GROUPINGS.some((g) => title === overTimeTitle(g))) return true;
    if (!title.endsWith(" over time")) return false;
    const inner = title.slice(0, -" over time".length);
    if (subject !== null && inner === subject) return true;
    const halves = splitBy(inner);
    if (!halves) return false;
    return (
      (subject !== null && halves.subject === subject) ||
      OVER_TIME_GROUPINGS.some((g) => g === halves.noun)
    );
  };
  return resolveStoredTitle(
    config,
    owns,
    overTimeTitle(groupBy, subject),
    subject === null ? LINE_BLANK_NAME : undefined,
  );
}

/** "Portfolio by type", or the measure's subject under a field Measure. */
export function portfolioByDimensionTitle(
  dimension: TitledDimension,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
  subject: string | null = null,
): string {
  return `${subject ?? "Portfolio"} by ${dimensionNoun(dimension, context)}`;
}

/** `dynamicTitle` for portfolio-map. Curated preset names ("Workflow map",
 *  "Treemap") are deliberately OUTSIDE the family — they read as authored and
 *  stick, exactly like a user rename. */
export function portfolioTitle(
  config: TitledConfig,
  dimension: TitledDimension,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
): string {
  const subject = measureSubject(config, context);
  const noun = dimensionNoun(dimension, context);
  const fieldInvolved = involvesField(dimension, subject);
  return resolveStoredTitle(
    config,
    (title) =>
      title === "Portfolio map" ||
      ownsBlankName(title, PORTFOLIO_BLANK_NAME, fieldInvolved) ||
      ownsByTitle(title, "Portfolio", subject, noun),
    portfolioByDimensionTitle(dimension, context, subject),
    fieldInvolved ? undefined : PORTFOLIO_BLANK_NAME,
  );
}

type OutcomeView = "summary" | "members" | "entities";

/** The card's name: what it counts (posted Updates) cut by what it cuts
 *  them on (their outcome). The per-entity view names its rows' kind; the
 *  members view is the summary now, so it shares the summary's name. */
export function outcomePerformanceViewTitle(view: OutcomeView): string {
  if (view === "entities") return "Update outcomes by Project";
  return "Update outcomes";
}

/** The viewer's own card on My Work. */
export const MY_UPDATE_OUTCOMES_TITLE = "My Update outcomes";

/** The names the card shipped under as "Outcome performance". Each was a
 *  seed or a derived name, never a user's words, so a stored one reads as the
 *  current name instead of sticking as a rename. */
const LEGACY_OUTCOME_TITLES: ReadonlyMap<string, string | null> = new Map([
  ["My outcome performance", MY_UPDATE_OUTCOMES_TITLE],
  // null: whatever the view derives now.
  ["Outcome performance", null],
  ["Outcome performance by member", null],
  ["Outcome performance by entity", null],
]);

/** `dynamicTitle` for outcome-performance. The family owns the "by Project"
 *  form and every legacy name; the bare "Update outcomes" is the curated
 *  managed-board name (Team Overview seeds it, and it is the module's static
 *  title), so it reads as authored and sticks — the same carve-out "Portfolio
 *  map" gets. */
export function outcomePerformanceTitle(config: TitledConfig, view: OutcomeView): string {
  const legacy = typeof config.title === "string" ? LEGACY_OUTCOME_TITLES.get(config.title) : undefined;
  if (legacy) return legacy;
  return resolveStoredTitle(
    config,
    (title) => LEGACY_OUTCOME_TITLES.has(title) || title === outcomePerformanceViewTitle("entities"),
    outcomePerformanceViewTitle(view),
  );
}
